<script setup lang="ts">
// Folder picker for a recording destination: browse the node's folders
// (quick picks: local recordings and each network share), create one, and
// take it. The path field next to it stays editable for a manual path.
import { gbText } from '../util'
import { onMounted, ref } from 'vue'
import { api, ApiError, type FsDir } from '../api'
import { useNode } from '../store'

const props = defineProps<{ start: string }>()
const emit = defineEmits<{ pick: [string]; close: [] }>()
const node = useNode()
const dir = ref<FsDir | null>(null)
const missing = ref('') // the folder asked for does not exist (yet)
const err = ref('')
const newName = ref('')

const roots = () => [
    { label: 'local recordings', path: '/var/lib/ese/recordings' },
    ...(node.config?.storage ?? []).map((s) => ({
        label: s.name || s.id,
        path: '/mnt/ese/' + s.id,
        st: node.status?.storage?.find((x) => x.id === s.id),
    })),
]

async function open(path: string) {
    err.value = ''
    missing.value = ''
    try {
        dir.value = await api.fs(path)
    } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
            missing.value = path // offer to create it; show its parent meanwhile
            const parent = path.replace(/\/[^/]+\/?$/, '') || '/'
            if (parent !== path)
                try {
                    dir.value = await api.fs(parent)
                } catch {
                    /* keep what we had */
                }
        } else err.value = e instanceof Error ? e.message : String(e)
    }
}
async function create(path: string) {
    try {
        await api.mkdir(path)
        newName.value = ''
        await open(path)
    } catch (e) {
        err.value = e instanceof Error ? e.message : String(e)
    }
}
const join = (a: string, b: string) => (a.endsWith('/') ? a : a + '/') + b
onMounted(() => open(props.start || '/var/lib/ese/recordings'))
</script>

<template>
    <div class="mt-1 mb-2.5 rounded-md border border-line-strong bg-bg-2 p-2">
        <div class="mb-1.5 flex flex-wrap items-center gap-1">
            <button
                v-for="r in roots()"
                :key="r.path"
                type="button"
                class="btn-ghost text-xs"
                :class="{ 'text-accent': dir?.path.startsWith(r.path) }"
                :title="r.path"
                @click="open(r.path)"
            >
                {{ r.label
                }}<span v-if="'st' in r && r.st && r.st.state !== 'ok'" class="text-bad">
                    · {{ r.st.state.replace('_', ' ') }}</span
                >
            </button>
        </div>
        <div v-if="dir" class="flex items-center gap-1.5 pt-0.5 pb-1 font-mono text-xs tabular-nums">
            <button
                v-if="dir.parent"
                type="button"
                class="btn-ghost text-xs"
                title="Up one folder"
                @click="open(dir.parent)"
            >
                ↑
            </button>
            <span class="min-w-0 flex-1 truncate">{{ dir.path }}</span>
            <span class="text-fg-3"
                >{{ gbText(dir.free_gb) }} free<template v-if="!dir.writable">
                    · <span class="text-bad">not writable by the node</span></template
                ></span
            >
        </div>
        <div v-if="missing" class="py-1 text-xs">
            {{ missing }} does not exist.
            <button type="button" class="btn-ghost text-xs" @click="create(missing)">create it</button>
        </div>
        <div v-if="dir" class="max-h-50 overflow-auto border-t border-line">
            <button
                v-for="d in dir.dirs"
                :key="d"
                type="button"
                class="block w-full rounded-none border-0 bg-transparent px-1.5 py-0.75 text-left text-xs text-fg hover:bg-bg-3"
                @click="open(join(dir.path, d))"
            >
                📁 {{ d }}
            </button>
            <div v-if="!dir.dirs.length" class="px-1.5 py-1 text-xs text-fg-3">no folders here</div>
        </div>
        <div v-if="err" class="text-xs text-bad">{{ err }}</div>
        <div v-if="dir" class="mt-1.5 flex items-center gap-1.5">
            <input
                v-model="newName"
                class="max-w-[180px] font-mono tabular-nums"
                placeholder="new folder"
                @keydown.enter.prevent="newName.trim() && create(join(dir.path, newName.trim()))"
            />
            <button
                type="button"
                class="btn-ghost text-xs"
                :disabled="!newName.trim() || !dir.writable"
                @click="create(join(dir.path, newName.trim()))"
            >
                create
            </button>
            <span class="min-w-0 flex-1" />
            <button type="button" class="btn-ghost text-xs" @click="emit('close')">cancel</button>
            <button type="button" class="btn-primary text-xs" :disabled="!dir.writable" @click="emit('pick', dir.path)">
                use this folder
            </button>
        </div>
    </div>
</template>
